import React, { useCallback, useEffect } from 'react';
import useEmblaCarousel from 'embla-carousel-react';
import { Card, CardContent } from './ui/card';
import { Star, Quote, ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from './ui/button';
import Avatar from './Avatar';

const TestimonialsCarousel = ({ testimonials }) => {
  const [emblaRef, emblaApi] = useEmblaCarousel({ 
    loop: true,
    align: 'start',
    slidesToScroll: 1,
    breakpoints: {
      '(min-width: 768px)': { slidesToScroll: 3 }
    }
  });

  const scrollPrev = useCallback(() => {
    if (emblaApi) emblaApi.scrollPrev();
  }, [emblaApi]);

  const scrollNext = useCallback(() => {
    if (emblaApi) emblaApi.scrollNext();
  }, [emblaApi]);

  // Auto-play carousel
  useEffect(() => {
    if (!emblaApi) return;

    const autoplay = setInterval(() => {
      emblaApi.scrollNext();
    }, 5000); // Scroll every 5 seconds

    return () => clearInterval(autoplay);
  }, [emblaApi]);

  return (
    <div className="relative">
      {/* Carousel Container */}
      <div className="overflow-hidden" ref={emblaRef}>
        <div className="flex -ml-4">
          {testimonials.map((testimonial) => (
            <div
              key={testimonial.id}
              className="flex-[0_0_100%] md:flex-[0_0_33.333%] pl-4 min-w-0"
            >
              <Card className="bg-white/5 border border-white/10 backdrop-blur-sm hover:border-orange-500/50 transition-all duration-300 h-full">
                <CardContent className="p-8">
                  <Quote className="w-10 h-10 text-orange-500 mb-4" />
                  
                  <div className="flex mb-4">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <Star key={`star-${i}`} className="w-5 h-5 fill-orange-500 text-orange-500" />
                    ))}
                  </div>

                  <p className="text-gray-300 mb-6 leading-relaxed italic min-h-[120px]">
                    "{testimonial.text}"
                  </p>

                  <div className="flex items-center space-x-4">
                    <Avatar initials={testimonial.initials} className="w-12 h-12 text-lg" />
                    <div>
                      <p className="font-bold text-white">{testimonial.name}</p>
                      <p className="text-sm text-gray-400">{testimonial.role}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          ))}
        </div>
      </div>

      {/* Navigation Buttons */}
      <div className="flex justify-center gap-4 mt-8">
        <Button
          onClick={scrollPrev}
          variant="outline"
          size="icon"
          className="bg-white/5 border-white/10 hover:bg-orange-500 hover:border-orange-500 transition-all"
        >
          <ChevronLeft className="w-5 h-5 text-white" />
        </Button>
        <Button
          onClick={scrollNext}
          variant="outline"
          size="icon"
          className="bg-white/5 border-white/10 hover:bg-orange-500 hover:border-orange-500 transition-all"
        >
          <ChevronRight className="w-5 h-5 text-white" />
        </Button>
      </div>
    </div>
  );
};

export default TestimonialsCarousel;
